import React, { useState } from 'react';
import { readViatorConsent, grantViatorConsent, safeAttr } from '../ViatorWidget';

/**
 * Viator affiliate banner, usable from page/column content via JsxParser:
 *
 *   <ViatorBanner partnerId="P00256778" campaign="AVUK-Left" />
 *
 * Optional props (defaults match the banner the client generated):
 *   width="120" height="600" selection="banner3" language="en"
 *   url="https://www.viator.com/"
 *
 * Same architecture as ViatorWidget: the raw embed can't run in content
 * (the parser strips <script>), so the banner renders inside its own
 * srcdoc iframe where Viator's banners.js runs natively and survives SPA
 * re-renders.
 *
 * Unlike the widget, the banner shows WITHOUT consent by default: it is
 * a static image + affiliate link (attribution via URL params) and sets
 * no cookies — verified empirically; only the image fetch from Viator's
 * CDN reaches a third party, which the privacy policy discloses. Pass
 * gated to require the shared aw_viator_ok consent anyway.
 */

const BANNER_SRC = 'https://partners.vtrcdn.com/static/scripts/banners/banners.js';

// The url attribute needs :/. — strip only characters that could break
// out of the attribute or inject markup.
const safeUrl = (v) => String(v || '').replace(/["'<>\\]/g, '');

const ViatorBanner = ({
  partnerId,
  campaign = '',
  width = 120,
  height = 600,
  selection = 'banner3',
  language = 'en',
  url = 'https://www.viator.com/',
  gated = false,
}) => {
  const [loaded, setLoaded] = useState(() => !gated || readViatorConsent());

  if (!partnerId) return null;

  const w = Number(width) || 120;
  const h = Number(height) || 600;

  if (!loaded) {
    return (
      <div className="aw-viator-gate">
        <div className="aw-viator-gate-icon" aria-hidden="true">🎟️</div>
        <p className="aw-viator-gate-title">Tours &amp; attractions</p>
        <p className="aw-viator-gate-sub">
          Days out, sights and experiences near and far.
        </p>
        <button
          type="button"
          className="aw-websearch-ddg-btn aw-viator-btn"
          onClick={() => { grantViatorConsent(); setLoaded(true); }}
        >
          Show attractions
        </button>
        <p className="aw-viator-gate-note">
          Provided by Viator, a TripAdvisor company. Loading connects your
          browser to Viator, which may set cookies.
        </p>
      </div>
    );
  }

  const doc =
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<style>html,body{margin:0;padding:0;overflow:hidden}</style></head><body>' +
    '<div data-id="viator-banner"' +
    ' data-partner-id="' + safeAttr(partnerId) + '"' +
    ' data-url="' + safeUrl(url) + '"' +
    ' data-banner-width="' + w + '"' +
    ' data-banner-height="' + h + '"' +
    ' data-banner-language="' + safeAttr(language) + '"' +
    ' data-banner-selection="' + safeAttr(selection) + '"' +
    ' data-campaign="' + safeAttr(campaign) + '"></div>' +
    '<script async src="' + BANNER_SRC + '"><' + '/script>' +
    '</body></html>';

  return (
    <iframe
      className="aw-viator-banner"
      title="Viator attractions banner"
      srcDoc={doc}
      scrolling="no"
      style={{ width: w, height: h, border: 0, display: 'block', margin: '0 auto', maxWidth: '100%' }}
      loading="lazy"
    />
  );
};

export default ViatorBanner;
